import React, { useState, Fragment, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import MercaditoLogo from "../assets/MercaditoAI_Logo.jpg";

import "./navbar8.css";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import PacoAvatar from "./PacoAvatar";
import useAuthSession from "../hooks/useAuthSession";
import { WEB_BASE_URL } from "../config/appUrls";
import { clearAuthSession } from "../utils/auth";

const Navbar8 = ({
  logoAlt = "Mercadito AI Logo",
  logoSrc = MercaditoLogo,
  heading,
  content,
  page1,
  page1Description,
  action1,
  page1ImageSrc = "/images/steps/step1-pick-plan.jpg",
  page1ImageAlt = "image",
  link1,
  link1Url = WEB_BASE_URL,
  linkUrlPage1 = WEB_BASE_URL,

  page2,
  page2Description,
  action2,
  page2ImageSrc = "/images/steps/step2-signup.jpg",
  page2ImageAlt = "image",
  link2,
  link2Url = WEB_BASE_URL,
  linkUrlPage2 = WEB_BASE_URL,

  page3,
  page3Description,
  page3ImageSrc = "/images/steps/step3-customize.jpg",
  page3ImageAlt = "image",
  link3,
  link3Url = WEB_BASE_URL,
  linkUrlPage3 = WEB_BASE_URL,

  page4,
  page4Description,
  page4ImageSrc = "/images/steps/step4-grow.jpg",
  page4ImageAlt = "image",
  link4,
  link4Url = WEB_BASE_URL,
  linkUrlPage4 = WEB_BASE_URL,

  setShowChatBot
}) => {
  const { t, i18n } = useTranslation();
  const auth = useAuthSession();
  const { token, language } = auth;

  // Read the exact selection from the dropdown event
  const handleLanguageChange = (e) => {
    const newLang = e.target.value; 
    i18n.changeLanguage(newLang);
    localStorage.setItem("mercadito_lang", newLang);
  };

  // Create a dynamic variable that is always perfectly in sync with i18next
  const activeLang = (i18n.language || "en").startsWith("en") ? "en" : "es";

  const [link5AccordionOpen, setLink5AccordionOpen] = useState(false);
  const [link5DropdownVisible, setLink5DropdownVisible] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [navDropdownOpen, setNavDropdownOpen] = useState(false);

  // References and handlers to prevent accidental closing across margins
  const navDropdownRef = useRef(null);
  const mobileNavDropdownRef = useRef(null);
  const leaveTimeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
    }
    setNavDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    // 250ms buffer to allow cursor to safely cross the gap
    leaveTimeoutRef.current = setTimeout(() => {
      setNavDropdownOpen(false);
    }, 250);
  };

  // Close dropdown when user clicks anywhere outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideDesktop =
        !navDropdownRef.current || !navDropdownRef.current.contains(event.target);
      const isOutsideMobile =
        !mobileNavDropdownRef.current || !mobileNavDropdownRef.current.contains(event.target);

      if (isOutsideDesktop && isOutsideMobile) {
        setNavDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const checkToken = () => {

      if (!token) {
        setIsLoggedIn(false);
        return;
      }

      // Optional: Check JWT expiration
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload.exp && Date.now() / 1000 > payload.exp) {
          clearAuthSession();
          setIsLoggedIn(false);
        } else {
          setIsLoggedIn(true);
        }
      } catch {
        // If not JWT, assume present token = valid
        setIsLoggedIn(true);
      }
    };

    checkToken();

    // Re-check if storage changes
    window.addEventListener("storage", checkToken);
    return () => window.removeEventListener("storage", checkToken);
  }, []);

  return (
    <header className="navbar8-container1">
      <header data-thq="thq-navbar" className="navbar8-navbar-interactive">
        <img
          alt={logoAlt}
          src={logoSrc}
          className="navbar8-image1"
        />
        <div data-thq="thq-navbar-nav" className="navbar8-desktop-menu">
          <nav className="navbar8-links1">
            <div
              href={link1Url}
              className="navbar8-link11 thq-link thq-body-small"
            >
              {link1 ?? (
                <Fragment>
                  <span className="navbar8-text18">
                    {t("home.navBar.link1")}
                  </span>
                </Fragment>
              )}
            </div>
            <div href={link2Url} className="thq-link thq-body-small">
              {link2 ?? (
                <Fragment>
                  <span className="navbar8-text16">
                    {t("home.navBar.link2")}
                  </span>
                </Fragment>
              )}
            </div>
            <div
              href={link3Url}
              target="_blank"
              rel="noreferrer noopener"
              className="navbar8-link31 thq-link thq-body-small"
            >
              {link3 ?? (
                <Fragment>
                  <span className="navbar8-text24">
                    {t("home.navBar.link3")}
                  </span>
                </Fragment>
              )}
            </div>
            <div
              onClick={() => setLink5DropdownVisible(!link5DropdownVisible)}
              className="navbar8-link4-dropdown-trigger"
            >
              <span className="thq-link thq-body-small">
                {link4 ?? (
                  <Fragment>
                    <span className="navbar8-text21">
                      {t("home.navBar.link4")}
                    </span>
                  </Fragment>
                )}
              </span>
              <div className="navbar8-icon-container1">
                {link5DropdownVisible === true && (
                  <div className="navbar8-container2">
                    <svg viewBox="0 0 1024 1024" className="navbar8-icon10">
                      <path d="M298 426h428l-214 214z"></path>
                    </svg>
                  </div>
                )}
                {link5DropdownVisible === false && (
                  <div className="navbar8-container3">
                    <svg viewBox="0 0 1024 1024" className="navbar8-icon12">
                      <path d="M426 726v-428l214 214z"></path>
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </nav>
          <div className="navbar8-buttons1">
            {/* ✅ DESKTOP SELECTOR */}
            {!isLoggedIn && (
              <a
                href="/login"
                className="navbar8-link11 thq-link thq-body-small"
                style={{ textDecoration: "none" }}
              >
                <Fragment>
                  <span className="navbar8-text18">{t("dropDown.login")}</span>
                </Fragment>
              </a>
            )}
            {isLoggedIn && (
              <div
                ref={navDropdownRef}
                className="navbar8-nav-dropdown"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div
                  className="navbar8-link11 thq-link thq-body-small"
                  onClick={() => setNavDropdownOpen((prev) => !prev)}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  <Fragment>
                    <span className="navbar8-text18">{t("dropDown.navigate")} ▾</span>
                  </Fragment>
                </div>

                {navDropdownOpen && (
                  <div
                    className="navbar8-simple-dropdown"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <a href="/dashboard" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.dashboard-page")}</a>
                    <a href="/support" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.support-page")}</a>
                    <a href="/qr-code" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.qr-page")}</a>
                    <a href="/clients" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.clients-page")}</a>
                    <a href="/crm-lite" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.crm-lite-page")}</a>
                    <a href="/analytics" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.analytics-page")}</a>
                    <a href="/scheduler" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.scheduler-page")}</a>
                    <a href="/commerce" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.commerce-page")}</a>
                    <a href="/dispatcher" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.dispatcher-page")}</a>
                    <a href="/tutorial" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.tutorial-page")}</a>
                  </div>
                )}
              </div>
            )}
            <select
              onChange={handleLanguageChange}
              value={activeLang}
              className="navbar8-action11 thq-button-animated thq-button-filled"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
            <button className="navbar8-action11 thq-button-animated thq-button-filled">
              <span>
                {action1 ?? (
                  <Fragment>
                    <span className="navbar8-text15">
                      {t("home.navBar.action1")}
                    </span>
                  </Fragment>
                )}
              </span>
            </button>
            <button className="navbar8-action21 thq-button-outline thq-button-animated">
              <span>
                {action2 ?? (
                  <Fragment>
                    <span className="navbar8-text27">
                      {t("home.navBar.action2")}
                    </span>
                  </Fragment>
                )}
              </span>
            </button>
          </div>
        </div>
        <div data-thq="thq-burger-menu" className="navbar8-burger-menu">
          <svg viewBox="0 0 1024 1024" className="navbar8-icon14">
            <path d="M128 554.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 298.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 810.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667z"></path>
          </svg>
        </div>
        <div data-thq="thq-mobile-menu" className="navbar8-mobile-menu">
          <div className="navbar8-nav">
            <div className="navbar8-top">
              <img
                alt={logoAlt}
                src={logoSrc}
                className="navbar8-logo"
              />
              <div data-thq="thq-close-menu" className="navbar8-close-menu">
                <svg viewBox="0 0 1024 1024" className="navbar8-icon16">
                  <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
                </svg>
              </div>
            </div>
            <nav className="navbar8-links2">
              <div
                href={link1Url}
                className="navbar8-link12 thq-link thq-body-small"
              >
                {link1 ?? (
                  <Fragment>
                    <span className="navbar8-text18">
                      {t("home.navBar.link1")}
                    </span>
                  </Fragment>
                )}
              </div>
              <div href={link2Url} className="thq-link thq-body-small">
                {link2 ?? (
                  <Fragment>
                    <span className="navbar8-text16">
                      {t("home.navBar.link2")}
                    </span>
                  </Fragment>
                )}
              </div>
              <div
                href={link3Url}
                className="navbar8-link32 thq-link thq-body-small"
              >
                {link3 ?? (
                  <Fragment>
                    <span className="navbar8-text24">
                      {t("home.navBar.link3")}
                    </span>
                  </Fragment>
                )}
              </div>
              <div className="navbar8-link4-accordion">
                <div
                  onClick={() => setLink5AccordionOpen(!link5AccordionOpen)}
                  className="navbar8-trigger"
                >
                  <span className="thq-link thq-body-small">
                    {link4 ?? (
                      <Fragment>
                        <span className="navbar8-text21">
                          {t("home.navBar.link4")}
                        </span>
                      </Fragment>
                    )}
                  </span>
                  <div className="navbar8-icon-container2">
                    {link5AccordionOpen === true && (
                      <div className="navbar8-container4">
                        <svg viewBox="0 0 1024 1024" className="navbar8-icon18">
                          <path d="M298 426h428l-214 214z"></path>
                        </svg>
                      </div>
                    )}
                    {link5AccordionOpen === false && (
                      <div className="navbar8-container5">
                        <svg viewBox="0 0 1024 1024" className="navbar8-icon20">
                          <path d="M426 726v-428l214 214z"></path>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                {link5AccordionOpen === true && (
                  <div className="navbar8-container6">
                    <div className="content-wrapper">
                      <h2 className="steps-heading">
                        {heading ?? (
                          <Fragment>{t("home.navBar.heading")}</Fragment>
                        )}
                      </h2>
                      <div
                        style={{
                          textAlign: "center",
                          marginTop: "1rem",
                          marginBottom: "1rem"
                        }}
                      >
                        <PacoAvatar
                          style={{ width: "180px", height: "auto" }}
                        />
                        <p className="steps-content">
                          {content ?? (
                            <Fragment>{t("home.navBar.content")}</Fragment>
                          )}
                        </p>
                      </div>

                      <div className="navbar8-menu-item1">
                        <img
                          alt={page1ImageAlt}
                          src={page1ImageSrc}
                          className="navbar8-page1-image1"
                        />
                        <div className="navbar8-content1">
                          <span className="navbar8-page11 thq-body-large">
                            {page1 ?? (
                              <Fragment>
                                <span className="navbar8-text17">
                                  {t("home.navBar.title1")}
                                </span>
                              </Fragment>
                            )}
                          </span>
                          <span className="thq-body-small">
                            {page1Description ?? (
                              <Fragment>
                                <span className="navbar8-text22">
                                  {t("home.navBar.description1")}
                                </span>
                              </Fragment>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="navbar8-menu-item2">
                        <img
                          alt={page2ImageAlt}
                          src={page2ImageSrc}
                          className="navbar8-page2-image1"
                        />
                        <div className="navbar8-content2">
                          <span className="navbar8-page21 thq-body-large">
                            {page2 ?? (
                              <Fragment>
                                <span className="navbar8-text20">
                                  {t("home.navBar.title2")}
                                </span>
                              </Fragment>
                            )}
                          </span>
                          <span className="thq-body-small">
                            {page2Description ?? (
                              <Fragment>
                                <span className="navbar8-text23">
                                  {t("home.navBar.description2")}
                                </span>
                              </Fragment>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="navbar8-menu-item3">
                        <img
                          alt={page3ImageAlt}
                          src={page3ImageSrc}
                          className="navbar8-page3-image1"
                        />
                        <div className="navbar8-content3">
                          <span className="navbar8-page31 thq-body-large">
                            {page3 ?? (
                              <Fragment>
                                <span className="navbar8-text25">
                                  {t("home.navBar.title3")}
                                </span>
                              </Fragment>
                            )}
                          </span>
                          <span className="thq-body-small">
                            {page3Description ?? (
                              <Fragment>
                                <span className="navbar8-text26">
                                  {t("home.navBar.description3")}
                                </span>
                              </Fragment>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="navbar8-menu-item4">
                        <img
                          alt={page4ImageAlt}
                          src={page4ImageSrc}
                          className="navbar8-page4-image1"
                        />
                        <div className="navbar8-content4">
                          <span className="navbar8-page41 thq-body-large">
                            {page4 ?? (
                              <Fragment>
                                <span className="navbar8-text19">
                                  {t("home.navBar.title4")}
                                </span>
                              </Fragment>
                            )}
                          </span>
                          <span className="thq-body-small">
                            {page4Description ?? (
                              <Fragment>
                                <span className="navbar8-text14">
                                  {t("home.navBar.description4")}
                                </span>
                              </Fragment>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </nav>
            <div className="navbar8-buttons2">
              {/* ✅ MOBILE SELECTOR */}
              {!isLoggedIn && (
                <a
                  href="/login"
                  className="navbar8-link12 thq-link thq-body-small"
                  style={{ textDecoration: "none" }}
                >
                  <Fragment>
                    <span className="navbar8-text18">{t("dropDown.login")}</span>
                  </Fragment>
                </a>
              )}
              {isLoggedIn && (
                <div 
                  ref={mobileNavDropdownRef}
                  className="navbar8-nav-dropdown"
                >
                  <div
                    className="navbar8-link11 thq-link thq-body-small"
                    onClick={() => setNavDropdownOpen((prev) => !prev)}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    <Fragment>
                      <span className="navbar8-text18">{t("dropDown.navigate")} ▾</span>
                    </Fragment>
                  </div>

                  {navDropdownOpen && (
                    <div className="navbar8-simple-dropdown">
                      <a href="/dashboard" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.dashboard-page")}</a>
                      <a href="/support" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.support-page")}</a>
                      <a href="/qr-code" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.qr-page")}</a>
                      <a href="/clients" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.clients-page")}</a>
                      <a href="/crm-lite" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.crm-lite-page")}</a>
                      <a href="/analytics" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.analytics-page")}</a>
                      <a href="/scheduler" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.scheduler-page")}</a>
                      <a href="/commerce" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.commerce-page")}</a>
                      <a href="/dispatcher" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.dispatcher-page")}</a>
                      <a href="/tutorial" onClick={() => setNavDropdownOpen(false)}>{t("dropDown.tutorial-page")}</a>
                    </div>
                  )}
                </div>
              )}
              <select
                onChange={handleLanguageChange}
                value={activeLang}
                className="navbar8-action11 thq-button-animated thq-button-filled"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
              <button className="thq-button-filled">
                <span>
                  {action1 ?? (
                    <Fragment>
                      <span className="navbar8-text15">
                        {t("home.navBar.action1")}
                      </span>
                    </Fragment>
                  )}
                </span>
              </button>
              <button className="thq-button-outline">
                <span>
                  {action2 ?? (
                    <Fragment>
                      <span className="navbar8-text27">
                        {t("home.navBar.action2")}
                      </span>
                    </Fragment>
                  )}
                </span>
              </button>
            </div>
          </div>
          <div className="navbar8-icon-group">
            {/* Instagram */}
            <a
              href="https://instagram.com/MercaditoAI"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg className="thq-icon-small" viewBox="0 0 1024 1024">
                <path
                  d="M512 378.7c-73.5 0-133.3 59.8-133.3 133.3s59.8 133.3 133.3 133.3 133.3-59.8 133.3-133.3-59.8-133.3-133.3-133.3z m0 328c-107.5 0-194.7-87.2-194.7-194.7S404.5 317.3 512 317.3s194.7 87.2 194.7 194.7-87.2 194.7-194.7 194.7zM691.2 320c-25.6 0-46.4-20.8-46.4-46.4s20.8-46.4 46.4-46.4 46.4 20.8 46.4 46.4-20.8 46.4-46.4 46.4z m123.7-46.4c0-68.2-55.5-123.7-123.7-123.7H332.8c-68.2 0-123.7 55.5-123.7 123.7v358.4c0 68.2 55.5 123.7 123.7 123.7h358.4c68.2 0 123.7-55.5 123.7-123.7V273.6zM512 704c-106 0-192-86-192-192s86-192 192-192 192 86 192 192-86 192-192 192z"
                  fill="currentColor"
                />
              </svg>
            </a>
            {/* Facebook */}
            <a
              href="https://facebook.com/MercaditoAI"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg className="thq-icon-small" viewBox="0 0 320 512">
                <path
                  d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06H293V6.26S259.29 0 225.36 0c-73.14 0-121.11 44.38-121.11 124.72v70.62H22.89V288h81.36v224h100.2V288z"
                  fill="currentColor"
                />
              </svg>
            </a>
            {/* TikTok */}
            <a
              href="https://tiktok.com/@MercaditoAI"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg className="thq-icon-small" viewBox="0 0 1024 1024">
                <path
                  d="M768 320c-41.6 0-80-13.6-112-36v224.8c0 129.6-104.8 235.2-234.4 235.2S187.2 638.4 187.2 508.8 292 273.6 421.6 273.6c15.2 0 29.6 2.4 43.2 6.4v108c-13.6-3.2-28-4.8-43.2-4.8-71.2 0-129.6 58.4-129.6 129.6s58.4 129.6 129.6 129.6 129.6-58.4 129.6-129.6V128h99.2c0 57.6 46.4 104 104 104h18.4v88z"
                  fill="currentColor"
                />
              </svg>
            </a>
            {/* LinkedIn */}
            <a
              href="https://linkedin.com/company/mercaditoai"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg className="thq-icon-small" viewBox="0 0 448 512">
                <path
                  d="M100.3 448H7.4V149.6h92.9zm-46.5-340.4C24 107.6 0 83.1 0 52.6 0 22.2 24 0 53.9 0s53.8 22.2 53.8 52.6c0 30.5-24 55-53.9 55zM447.8 448h-92.7V302.4c0-34.7-12.4-58.4-43.4-58.4-23.7 0-37.7 15.9-43.9 31.3-2.3 5.6-2.8 13.3-2.8 21.1V448h-92.8s1.2-264.7 0-292.4h92.8v41.4c12.3-19 34.2-46.1 83.3-46.1 60.8 0 106.3 39.7 106.3 125.2V448z"
                  fill="currentColor"
                />
              </svg>
            </a>
            {/* X (Twitter) */}
            <a
              href="https://x.com/MercaditoAI"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg className="thq-icon-small" viewBox="0 0 512 512">
                <path
                  d="M512 64.6l-197.5 211.9L508.3 448H412.6L267.3 284.4 98 448H0L216.8 226.3 20.7 64.6h105.6l133.2 124.2L412 64.6z"
                  fill="currentColor"
                />
              </svg>
            </a>
          </div>
        </div>
        {link5DropdownVisible === true && (
          <div className="navbar8-container7 thq-box-shadow">
            <h2 className="steps-heading">
              {heading ?? <Fragment>{t("home.navBar.heading")}</Fragment>}
            </h2>
            <div
              style={{
                textAlign: "center",
                marginTop: "1rem",
                marginBottom: "1rem"
              }}
            >
              <PacoAvatar style={{ width: "180px", height: "auto" }} />
              <p className="steps-content">
                {content ?? (
                  <Fragment>{t("home.navBar.content")}</Fragment>
                )}
              </p>
            </div>
            <div className="navbar8-link5-menu-list">
              <div className="navbar8-menu-item5">
                <img
                  alt={page1ImageAlt}
                  src={page1ImageSrc}
                  className="navbar8-page1-image2 thq-img-ratio-1-1"
                />
                <div className="navbar8-content5">
                  <span className="navbar8-page12 thq-body-large">
                    {page1 ?? (
                      <Fragment>
                        <span className="navbar8-text17">
                          {t("home.navBar.title1")}
                        </span>
                      </Fragment>
                    )}
                  </span>
                  <span className="thq-body-small">
                    {page1Description ?? (
                      <Fragment>
                        <span className="navbar8-text22">
                          {t("home.navBar.description1")}
                        </span>
                      </Fragment>
                    )}
                  </span>
                </div>
              </div>
              <div className="navbar8-menu-item6">
                <img
                  alt={page2ImageAlt}
                  src={page2ImageSrc}
                  className="navbar8-page2-image2 thq-img-ratio-1-1"
                />
                <div className="navbar8-content6">
                  <span className="navbar8-page22 thq-body-large">
                    {page2 ?? (
                      <Fragment>
                        <span className="navbar8-text20">
                          {t("home.navBar.title2")}
                        </span>
                      </Fragment>
                    )}
                  </span>
                  <span className="thq-body-small">
                    {page2Description ?? (
                      <Fragment>
                        <span className="navbar8-text23">
                          {t("home.navBar.description2")}
                        </span>
                      </Fragment>
                    )}
                  </span>
                </div>
              </div>
              <div className="navbar8-menu-item7">
                <img
                  alt={page3ImageAlt}
                  src={page3ImageSrc}
                  className="navbar8-page3-image2 thq-img-ratio-1-1"
                />
                <div className="navbar8-content7">
                  <span className="navbar8-page32 thq-body-large">
                    {page3 ?? (
                      <Fragment>
                        <span className="navbar8-text25">
                          {t("home.navBar.title3")}
                        </span>
                      </Fragment>
                    )}
                  </span>
                  <span className="thq-body-small">
                    {page3Description ?? (
                      <Fragment>
                        <span className="navbar8-text26">
                          {t("home.navBar.description3")}
                        </span>
                      </Fragment>
                    )}
                  </span>
                </div>
              </div>
              <div className="navbar8-menu-item8">
                <img
                  alt={page4ImageAlt}
                  src={page4ImageSrc}
                  className="navbar8-page4-image2 thq-img-ratio-1-1"
                />
                <div className="navbar8-content8">
                  <span className="navbar8-page42 thq-body-large">
                    {page4 ?? (
                      <Fragment>
                        <span className="navbar8-text19">
                          {t("home.navBar.title4")}
                        </span>
                      </Fragment>
                    )}
                  </span>
                  <span className="thq-body-small">
                    {page4Description ?? (
                      <Fragment>
                        <span className="navbar8-text14">
                          {t("home.navBar.description4")}
                        </span>
                      </Fragment>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
      {link5DropdownVisible === true && (
        <div
          onClick={() => setLink5DropdownVisible(false)}
          className="navbar8-container8"
        ></div>
      )}
    </header>
  );
};

Navbar8.propTypes = {
  logoAlt: PropTypes.string,
  logoSrc: PropTypes.string,
  heading: PropTypes.element,
  content: PropTypes.element,

  page1: PropTypes.element,
  page1Description: PropTypes.element,
  action1: PropTypes.element,
  page1ImageSrc: PropTypes.string,
  page1ImageAlt: PropTypes.string,
  link1: PropTypes.element,
  link1Url: PropTypes.string,
  linkUrlPage1: PropTypes.string,

  page2: PropTypes.element,
  page2Description: PropTypes.element,
  action2: PropTypes.element,
  page2ImageSrc: PropTypes.string,
  page2ImageAlt: PropTypes.string,
  link2: PropTypes.element,
  link2Url: PropTypes.string,
  linkUrlPage2: PropTypes.string,

  page3: PropTypes.element,
  page3Description: PropTypes.element,
  page3ImageSrc: PropTypes.string,
  page3ImageAlt: PropTypes.string,
  link3: PropTypes.element,
  link3Url: PropTypes.string,
  linkUrlPage3: PropTypes.string,

  page4: PropTypes.element,
  page4Description: PropTypes.element,
  page4ImageSrc: PropTypes.string,
  page4ImageAlt: PropTypes.string,
  link4: PropTypes.element,
  linkUrlPage4: PropTypes.string
};
export default Navbar8;
